// src/pages/ValmikiContact.jsx
// VALMIKI AI LAB — Contact page (Page 6 of the 6-page Phase 1 architecture)
// ONE self-contained file: Navbar + Footer + all CSS + the four forms live here (same design system as the other pages).
//
// FOUR FORMS: Student · College Representative · Corporate / Industry · Faculty / Mentor
//   Deep links:  /valmiki/contact?type=student | college | industry | faculty
//   (the Partner page already links to ?type=college and ?type=industry)
//
// CRM REQUIREMENT — every form feeds ONE central lead tracker. Each submission is sent as a single record:
//   leadType · source · createdAt (date) · owner · status · nextFollowUpDate · fields{…form answers…}
// Set CRM_ENDPOINT to your CRM / lead-tracker webhook (Zapier, Make, HubSpot form API, Google Apps Script, your own API…).
// The request is multipart/form-data: a JSON string in `lead`, plus the CV file in `cv` for faculty leads.
// While CRM_ENDPOINT is empty the page runs in demo mode: it logs the record to the console and shows success.
// Owner / status / follow-up are set here as sensible defaults; your CRM should treat them as the starting values.
//
// MOTION: the hero list highlights the four options in turn; switching forms re-draws the gold top line and
// brings the fields in one after another; a successful send draws a tick. All motion is off for prefers-reduced-motion.
// Lead popup: <ValmikiLeadForm /> opens itself after its own timer (see that file).

import React, { useEffect, useMemo, useRef, useState } from "react";
import ValmikiFooter from "./ValmikiFooter";
import ValmikiLeadForm from "./ValmikiLeadPopup";


/* ───────────────────────── CONFIG ───────────────────────── */

const CRM_ENDPOINT = ""; // e.g. "https://hooks.example.com/valmiki-leads"
const DEFAULT_SOURCE = "Website – Contact page";
const FOLLOW_UP_BUSINESS_DAYS = 2; // next follow-up date = submission date + this many business days
const MAX_CV_MB = 5;

// Default lead owner per lead type. Leave "" to send as "Unassigned" and let the CRM auto-assign.
const OWNER_BY_TYPE = { student: "", college: "", corporate: "", faculty: "" };

// OFFICE LOCATION — REAL contact details filled in.
// Set `mapUrl` to a specific Google Maps place link if you want exact pin placement;
// leaving it "" makes "Get directions" search the address automatically.
const OFFICE = {
  name: "VALMIKI AI LAB",
  lines: [
    "VALMIKI AI LAB",
    "P S Abacus – 703, 7th Floor",
    "NH12, Action Area IIE, Newtown",
  ],
  cityLine: "West Bengal 700161, India",
  phone: "+91 90000 00000",
  email: "hello@valmikiailab.com",
  hours: "Mon–Sat · 10:00 AM – 6:00 PM IST",
  mapUrl: "",
};
/* ───────────────────────── CONTENT ───────────────────────── */

const STUDENT_STATUS = [
  "First year",
  "Second year",
  "Third year",
  "Final year",
  "Recent graduate",
  "Working professional",
  "Other",
];

const PARTNERSHIP_INTEREST = [
  "Internship partnerships",
  "Industry mentorship",
  "Student project challenges",
  "Talent pipeline engagement",
  "Entry-level hiring opportunities",
  "Other",
];

// span: "full" fields take the whole row. optional: true shows an "Optional" tag; every other field is required.
const TYPES = [
  {
    id: "student",
    tab: "Student",
    cta: "Explore Learning Opportunities",
    blurb: "Learn applied AI and build a portfolio.",
    fields: [
      { name: "fullName", label: "Full name", type: "text", autoComplete: "name" },
      { name: "mobile", label: "Mobile number", type: "tel", autoComplete: "tel" },
      { name: "email", label: "Email", type: "email", autoComplete: "email" },
      { name: "institution", label: "College / Institution", type: "text", autoComplete: "organization" },
      { name: "city", label: "City", type: "text", autoComplete: "address-level2" },
      { name: "status", label: "Current year / status", type: "select", options: STUDENT_STATUS },
      { name: "message", label: "Message", type: "textarea", optional: true, span: "full" },
    ],
  },
  {
    id: "college",
    tab: "College Representative",
    cta: "Discuss a College Partnership",
    blurb: "Bring the program to your campus.",
    fields: [
      { name: "name", label: "Name", type: "text", autoComplete: "name" },
      { name: "designation", label: "Designation", type: "text", autoComplete: "organization-title" },
      { name: "institution", label: "Institution name", type: "text", autoComplete: "organization" },
      { name: "email", label: "Official email", type: "email", autoComplete: "email" },
      { name: "mobile", label: "Mobile number", type: "tel", autoComplete: "tel" },
      { name: "city", label: "City", type: "text", autoComplete: "address-level2" },
      { name: "studentStrength", label: "Approximate student strength", type: "number", optional: true, min: 1 },
      { name: "message", label: "Message", type: "textarea", span: "full" },
    ],
  },
  {
    id: "corporate",
    tab: "Corporate / Industry",
    cta: "Partner With VALMIKI",
    blurb: "Offer internships, mentoring or hiring.",
    fields: [
      { name: "name", label: "Name", type: "text", autoComplete: "name" },
      { name: "company", label: "Company", type: "text", autoComplete: "organization" },
      { name: "designation", label: "Designation", type: "text", autoComplete: "organization-title" },
      { name: "email", label: "Email", type: "email", autoComplete: "email" },
      { name: "mobile", label: "Mobile", type: "tel", autoComplete: "tel" },
      { name: "interest", label: "Partnership interest", type: "select", options: PARTNERSHIP_INTEREST },
      { name: "message", label: "Message", type: "textarea", span: "full" },
    ],
  },
  {
    id: "faculty",
    tab: "Faculty / Mentor",
    cta: "Join Our Faculty Network",
    blurb: "Teach or mentor in our specialist network.",
    fields: [
      { name: "name", label: "Name", type: "text", autoComplete: "name" },
      { name: "specialization", label: "Specialization", type: "text" },
      { name: "experience", label: "Years of experience", type: "number", min: 0, max: 60 },
      { name: "link", label: "LinkedIn / portfolio link", type: "url", placeholder: "https://" },
      { name: "email", label: "Email", type: "email", autoComplete: "email" },
      { name: "mobile", label: "Mobile", type: "tel", autoComplete: "tel" },
      { name: "cv", label: "Upload CV", type: "file", accept: ".pdf,.doc,.docx", span: "full",
        hint: `PDF or Word file, up to ${MAX_CV_MB} MB.` },
    ],
  },
];

// Lets links like ?type=industry work, whichever word the other pages use.
const TYPE_ALIASES = {
  student: "student",
  students: "student",
  college: "college",
  colleges: "college",
  industry: "corporate",
  corporate: "corporate",
  faculty: "faculty",
  mentor: "faculty",
};

/* ───────────────────────── STYLES (in-page) ───────────────────────── */

const css = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,400&display=swap');

.vl-root{
  --ink:#0B1626; --ink-2:#132238; --ink-3:#08111E;
  --paper:#F3F5F8; --white:#FFFFFF;
  --gold:#C8A24A; --gold-hi:#E0BC62; --gold-deep:#7D5F12;
  --slate:#4F5C73; --on-ink:#F3F5F8; --on-ink-mute:rgba(243,245,248,.72);
  --line:rgba(11,22,38,.14); --line-ink:rgba(243,245,248,.16);
  --danger:#A32626;
  --serif:'Newsreader',Georgia,'Times New Roman',serif;
  --sans:'IBM Plex Sans','Segoe UI',Arial,sans-serif;
  --mono:'IBM Plex Mono',ui-monospace,Menlo,Consolas,monospace;
  font-family:var(--sans); color:var(--ink); background:var(--paper);
  line-height:1.6; -webkit-font-smoothing:antialiased; margin:0;
}
.vl-root *{box-sizing:border-box;}
:where(.vl-root) :where(h1,h2,h3,p,ul,ol,fieldset,legend){margin:0;padding:0;}
:where(.vl-root) :where(ul,ol){list-style:none;}
:where(.vl-root) a{color:inherit;}
.vl-root :focus-visible{outline:2px solid var(--gold-hi);outline-offset:3px;}

.vl-wrap{max-width:1120px;margin:0 auto;padding:0 24px;}
.vl-section{padding:104px 0;}
.vl-dark{background:var(--ink);color:var(--on-ink);}

.vl-h2{font-family:var(--serif);font-weight:500;font-size:clamp(2rem,4vw,3rem);line-height:1.12;letter-spacing:-.015em;}
.vl-lead{font-size:1.125rem;line-height:1.65;max-width:56ch;}
.vl-muted{color:var(--slate);}

/* buttons */
@property --vl-a{syntax:"<angle>";inherits:false;initial-value:0deg;}
.vl-actions{display:flex;flex-wrap:wrap;gap:14px;}
.vl-btn{
  --fill:#162B47; --edge:var(--gold); --line:var(--gold);
  --wipe:var(--gold); --hover-text:var(--ink); --text:var(--on-ink);
  position:relative;overflow:hidden;isolation:isolate;
  display:inline-flex;align-items:center;justify-content:center;padding:14px 28px;border-radius:2px;
  font:600 15px/1 var(--sans);text-decoration:none;cursor:pointer;color:var(--text);
  border:1px solid var(--edge);
  background-color:var(--fill);
  background-image:linear-gradient(var(--wipe),var(--wipe));
  background-repeat:no-repeat;background-origin:border-box;background-position:left center;background-size:0% 100%;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s,border-color .3s ease;
}
.vl-btn::before{
  content:"";position:absolute;inset:0;padding:2px;border-radius:inherit;pointer-events:none;
  background:conic-gradient(from var(--vl-a),
    transparent 0deg,var(--line) 55deg,transparent 56deg,
    transparent 180deg,var(--line) 235deg,transparent 236deg);
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  -webkit-mask-composite:xor;
  mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  mask-composite:exclude;
  animation:vl-orbit 3.2s linear infinite;
}
@keyframes vl-orbit{to{--vl-a:360deg;}}
.vl-btn:hover,.vl-btn:focus-visible{background-size:100% 100%;color:var(--hover-text);border-color:var(--wipe);}
.vl-btn:disabled{opacity:.6;cursor:progress;}
.vl-btn:disabled::before{animation:none;}
.vl-btn-gold{animation:vl-breathe 4s ease-in-out infinite;}
@keyframes vl-breathe{
  0%,100%{background-color:#162B47;box-shadow:0 6px 14px rgba(0,0,0,.35);}
  50%{background-color:#24426B;box-shadow:0 6px 18px rgba(200,162,74,.30);}
}
.vl-btn-line-ink{--fill:transparent;--edge:var(--ink);--line:var(--ink);--wipe:var(--ink);--hover-text:var(--paper);--text:var(--ink);}
.vl-btn-ink{--fill:var(--ink);--edge:var(--gold);--line:var(--gold);--wipe:var(--gold);--hover-text:var(--ink);--text:var(--paper);}
@media (prefers-reduced-motion:reduce){
  .vl-btn,.vl-toggle{transition:none;}
  .vl-btn::before{animation:none;}
  .vl-btn-gold{animation:none;}
}

/* header */
.vl-header{position:sticky;top:0;z-index:50;background:var(--ink);border-bottom:1px solid var(--line-ink);}
.vl-header-row{display:flex;align-items:center;justify-content:space-between;min-height:68px;gap:24px;}
.vl-brand{display:flex;align-items:center;gap:12px;text-decoration:none;color:var(--on-ink);
  font:600 17px/1 var(--sans);letter-spacing:.14em;}
.vl-nav{display:flex;align-items:center;gap:26px;}
.vl-nav a:not(.vl-btn){font-size:12.5px;font-weight:500;letter-spacing:.09em;text-decoration:none;color:var(--on-ink-mute);
  padding:6px 0;border-bottom:1.5px solid transparent;transition:color .15s,border-color .15s;}
.vl-nav a:not(.vl-btn):hover{color:var(--on-ink);}
.vl-nav a[aria-current="page"]{color:var(--on-ink);border-bottom-color:var(--gold);}
.vl-nav .vl-btn{padding:11px 20px;font-size:12.5px;letter-spacing:.09em;}
.vl-toggle{display:none;background-color:transparent;
  background-image:linear-gradient(var(--paper),var(--paper));background-repeat:no-repeat;
  background-origin:border-box;background-position:left center;background-size:0% 100%;
  border:1px solid var(--line-ink);color:var(--on-ink);
  width:42px;height:42px;border-radius:3px;font-size:20px;cursor:pointer;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s;}
.vl-toggle:hover,.vl-toggle:focus-visible{background-size:100% 100%;color:var(--ink);}
.vl-mobile{display:none;background:var(--ink);border-top:1px solid var(--line-ink);padding:8px 24px 22px;}
.vl-mobile a:not(.vl-btn){display:block;padding:14px 0;border-bottom:1px solid var(--line-ink);text-decoration:none;
  font-size:14px;letter-spacing:.08em;color:var(--on-ink);}
.vl-mobile .vl-btn{margin-top:18px;width:100%;}
@media (max-width:1000px){
  .vl-nav{display:none;}
  .vl-toggle{display:block;}
  .vl-mobile.open{display:block;}
}

/* ── Contact popup / modal (opened by gold CTA) ── */
.vl-modal-backdrop{position:fixed;inset:0;z-index:200;background:rgba(8,17,30,.72);
  backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;padding:24px;
  animation:vl-modal-in .2s ease;}
.vl-modal{position:relative;width:100%;max-width:520px;background:var(--white);color:var(--ink);
  border-radius:10px;padding:36px 36px 32px;box-shadow:0 30px 80px rgba(0,0,0,.45);
  animation:vl-modal-pop .25s cubic-bezier(.2,.7,.2,1);}
.vl-modal h2{font-family:var(--serif);font-weight:500;font-size:1.75rem;line-height:1.2;
  margin:0 0 8px;letter-spacing:-.01em;color:var(--ink);}
.vl-modal p{font-size:15px;line-height:1.6;color:var(--slate);margin:0 0 24px;}
.vl-modal-close{position:absolute;top:14px;right:14px;width:34px;height:34px;border-radius:50%;
  border:1px solid rgba(11,22,38,.15);background:transparent;color:var(--ink);font-size:18px;
  cursor:pointer;display:inline-flex;align-items:center;justify-content:center;
  transition:background .18s,color .18s;}
.vl-modal-close:hover{background:var(--ink);color:var(--white);}
.vl-modal form{display:grid;gap:14px;}
.vl-modal label{display:block;font-size:13px;font-weight:600;color:var(--ink);margin-bottom:6px;
  letter-spacing:.02em;}
.vl-modal input,.vl-modal textarea{
  width:100%;padding:12px 14px;font:400 14px/1.4 var(--sans);
  border:1px solid rgba(11,22,38,.18);border-radius:3px;background:var(--white);color:var(--ink);
  transition:border-color .18s,box-shadow .18s;}
.vl-modal input:focus,.vl-modal textarea:focus{
  outline:none;border-color:var(--gold);box-shadow:0 0 0 3px rgba(200,162,74,.18);}
.vl-modal textarea{resize:vertical;min-height:96px;}
.vl-modal .vl-btn{width:100%;justify-content:center;margin-top:6px;}
@keyframes vl-modal-in{from{opacity:0;}to{opacity:1;}}
@keyframes vl-modal-pop{from{opacity:0;transform:translateY(12px) scale(.98);}
  to{opacity:1;transform:none;}}
@media (max-width:560px){
  .vl-modal{padding:28px 22px 24px;}
  .vl-modal h2{font-size:1.5rem;}
}

/* ───────── hero ───────── */
.vl-hero{position:relative;overflow:hidden;background-color:var(--ink);color:var(--on-ink);padding:88px 0 104px;
  background-image:linear-gradient(rgba(243,245,248,.035) 1px,transparent 1px),
                   linear-gradient(90deg,rgba(243,245,248,.035) 1px,transparent 1px);
  background-size:56px 56px;}
.vl-hero-grid{position:relative;z-index:1;display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:center;}
.vl-h1{font-family:var(--serif);font-weight:500;font-size:clamp(2.4rem,5vw,4rem);line-height:1.05;
  letter-spacing:-.025em;margin-bottom:24px;}
.vl-hero .vl-lead{color:var(--on-ink-mute);}
.vl-hero-fx{position:absolute;inset:0;pointer-events:none;overflow:hidden;}
.vl-hero-glow{position:absolute;right:-6%;top:6%;width:560px;height:560px;border-radius:50%;
  background:radial-gradient(circle,rgba(200,162,74,.18),transparent 65%);}

/* hero chooser: four ways to connect */
.vl-choose{background:var(--ink-2);border:1px solid var(--line-ink);border-radius:10px;padding:22px 24px 10px;
  box-shadow:0 24px 60px rgba(0,0,0,.35);}
.vl-choose-head{display:flex;justify-content:space-between;align-items:baseline;gap:12px;
  padding-bottom:14px;border-bottom:1px solid var(--line-ink);}
.vl-choose-title{font:600 15px/1.3 var(--sans);}
.vl-choose-tag{font:400 12px/1 var(--mono);color:var(--on-ink-mute);}
.vl-choice{position:relative;display:block;width:100%;text-align:left;cursor:pointer;color:inherit;
  background:transparent;border:0;border-bottom:1px solid var(--line-ink);padding:18px 16px 18px 18px;font:inherit;}
.vl-choice:last-child{border-bottom:0;}
.vl-choice::before{content:"";position:absolute;left:0;top:14px;bottom:14px;width:3px;background:var(--gold);
  transform:scaleY(0);transform-origin:top;transition:transform .3s ease;}
.vl-choice:hover::before,.vl-choice:focus-visible::before,.vl-choice[aria-pressed="true"]::before{transform:scaleY(1);}
.vl-choice-name{display:block;font-family:var(--serif);font-size:1.4rem;line-height:1.2;}
.vl-choice-cta{display:block;font:400 13px/1.5 var(--mono);color:var(--gold-hi);margin-top:4px;}
.vl-choice-blurb{display:block;font-size:14px;color:var(--on-ink-mute);margin-top:2px;}
.vl-choice[aria-pressed="true"] .vl-choice-name{color:var(--gold-hi);}

@media (prefers-reduced-motion:no-preference){
  .vl-hero{animation:vl-grid 10s linear infinite;}
  .vl-hero-glow{animation:vl-glow 4s ease-in-out infinite alternate;}
  .vl-h1,.vl-hero .vl-lead{animation:vl-rise .5s cubic-bezier(.2,.7,.2,1) both;}
  .vl-h1{animation-delay:.05s;}
  .vl-hero .vl-lead{animation-delay:.2s;}
  .vl-choose{animation:vl-panel-in .55s cubic-bezier(.2,.7,.2,1) .1s both;}
  /* attention loop: each option's gold bar flashes in turn, 2s apart */
  .vl-choice:not([aria-pressed="true"])::before{animation:vl-flash 8s ease-in-out infinite;animation-delay:calc(1.2s + var(--i) * 2s);}
}
@keyframes vl-grid{to{background-position:56px 56px,56px 56px;}}
@keyframes vl-glow{to{transform:translate(-50px,40px) scale(1.12);}}
@keyframes vl-rise{from{opacity:0;transform:translateY(22px);}to{opacity:1;transform:none;}}
@keyframes vl-panel-in{from{opacity:0;transform:translateX(28px);}to{opacity:1;transform:none;}}
@keyframes vl-flash{0%{transform:scaleY(0);}10%,22%{transform:scaleY(1);}32%,100%{transform:scaleY(0);}}

/* ───────── form section ───────── */
.vl-form-wrap{max-width:860px;margin:0 auto;}
.vl-tabs{display:flex;flex-wrap:wrap;gap:0 28px;border-bottom:1px solid var(--line);margin-bottom:44px;}
.vl-tab{background:none;border:0;cursor:pointer;font:500 15px/1 var(--sans);color:var(--slate);
  padding:14px 0;border-bottom:2px solid transparent;margin-bottom:-1px;transition:color .15s,border-color .15s;}
.vl-tab:hover{color:var(--ink);}
.vl-tab[aria-selected="true"]{color:var(--ink);border-bottom-color:var(--gold);font-weight:600;}

.vl-panel{position:relative;background:var(--white);border:1px solid var(--line);border-radius:6px;padding:44px 44px 40px;}
.vl-panel::before{content:"";position:absolute;left:-1px;right:-1px;top:-1px;height:3px;background:var(--gold);
  border-radius:6px 6px 0 0;transform-origin:left;}
.vl-form-title{font-family:var(--serif);font-weight:500;font-size:clamp(1.7rem,3vw,2.2rem);line-height:1.15;letter-spacing:-.01em;
  margin-bottom:8px;outline:none;}
.vl-form-note{font-size:14px;color:var(--slate);margin-bottom:32px;}

.vl-fields{display:grid;grid-template-columns:1fr 1fr;gap:24px 28px;}
.vl-field{display:flex;flex-direction:column;gap:6px;min-width:0;}
.vl-field.full{grid-column:1 / -1;}
.vl-label{display:flex;align-items:baseline;justify-content:space-between;gap:12px;font-size:14px;font-weight:600;}
.vl-opt{font:400 12px/1 var(--mono);color:var(--slate);}
.vl-input{width:100%;font:400 16px/1.4 var(--sans);color:var(--ink);background:var(--paper);
  border:1px solid rgba(11,22,38,.28);border-radius:3px;padding:12px 14px;transition:border-color .15s,background-color .15s;}
.vl-input:hover{border-color:rgba(11,22,38,.5);}
.vl-input:focus{outline:2px solid var(--gold);outline-offset:1px;border-color:var(--gold-deep);background:var(--white);}
textarea.vl-input{min-height:132px;resize:vertical;}
select.vl-input{appearance:none;-webkit-appearance:none;padding-right:40px;cursor:pointer;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%230B1626' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat:no-repeat;background-position:right 14px center;}
input[type="file"].vl-input{padding:10px 12px;cursor:pointer;}
input[type="file"].vl-input::file-selector-button{font:600 14px var(--sans);margin-right:14px;padding:8px 14px;border:1px solid var(--ink);
  background:var(--ink);color:var(--paper);border-radius:2px;cursor:pointer;}
.vl-hint{font-size:13px;color:var(--slate);}
.vl-input[aria-invalid="true"]{border-color:var(--danger);background:#FFF7F7;}
.vl-error{font-size:13.5px;color:var(--danger);font-weight:500;}
.vl-hp{position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden;}

.vl-form-foot{margin-top:32px;display:flex;flex-wrap:wrap;align-items:center;gap:20px 28px;}
.vl-privacy{font-size:13px;color:var(--slate);max-width:46ch;}
.vl-alert{margin-top:24px;padding:14px 16px;border-left:3px solid var(--danger);background:#FFF7F7;font-size:15px;color:var(--danger);}

/* success */
.vl-done{text-align:left;padding:8px 0 4px;}
.vl-done-mark{width:56px;height:56px;border-radius:50%;border:2px solid var(--gold);display:flex;align-items:center;justify-content:center;margin-bottom:24px;}
.vl-done-mark path{stroke-dasharray:30;stroke-dashoffset:0;}
.vl-done h3{font-family:var(--serif);font-weight:500;font-size:clamp(1.7rem,3vw,2.2rem);line-height:1.15;margin-bottom:12px;}
.vl-done p{max-width:52ch;margin-bottom:28px;}

/* motion that answers the person's action: switching forms and sending */
@media (prefers-reduced-motion:no-preference){
  .vl-panel::before{animation:vl-line .7s cubic-bezier(.5,0,.3,1) both;}
  .vl-field{animation:vl-rise .45s cubic-bezier(.2,.7,.2,1) both;animation-delay:calc(.12s + var(--i) * .05s);}
  .vl-done-mark{animation:vl-pop .45s cubic-bezier(.2,.7,.2,1) both;}
  .vl-done-mark path{animation:vl-draw .5s ease .3s both;}
}
@keyframes vl-line{from{transform:scaleX(0);}to{transform:scaleX(1);}}
@keyframes vl-pop{from{transform:scale(.6);opacity:0;}to{transform:none;opacity:1;}}
@keyframes vl-draw{from{stroke-dashoffset:30;}to{stroke-dashoffset:0;}}

/* ───────── office location ───────── */
.vl-office{display:grid;grid-template-columns:1fr 1fr;gap:72px;align-items:center;}
.vl-office .vl-h2{margin-bottom:28px;}
.vl-office-name{font:600 14px/1.4 var(--sans);color:var(--gold-hi);margin-bottom:8px;}
.vl-office-addr{font-style:normal;font-family:var(--serif);font-size:clamp(1.4rem,2.4vw,1.85rem);line-height:1.4;}
.vl-office-meta{margin-top:28px;border-top:1px solid var(--line-ink);max-width:30rem;}
.vl-office-meta li{display:flex;gap:18px;padding:14px 0;border-bottom:1px solid var(--line-ink);font-size:15px;}
.vl-office-meta span:first-child{flex:none;width:64px;color:var(--on-ink-mute);}
.vl-office-meta a{text-decoration:none;border-bottom:1px solid var(--gold);}
.vl-office-meta a:hover{color:var(--gold-hi);}

.vl-pinbox{position:relative;border:1px solid var(--line-ink);border-radius:10px;min-height:300px;overflow:hidden;
  background-color:var(--ink-2);
  background-image:linear-gradient(rgba(243,245,248,.05) 1px,transparent 1px),
                   linear-gradient(90deg,rgba(243,245,248,.05) 1px,transparent 1px);
  background-size:40px 40px;
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:28px;padding:40px 24px;}
.vl-pin{position:relative;width:64px;height:64px;display:flex;align-items:center;justify-content:center;}
.vl-pin svg{position:relative;z-index:1;display:block;}
.vl-pin-ring{position:absolute;inset:0;border-radius:50%;border:1.5px solid var(--gold);opacity:.5;}
@media (prefers-reduced-motion:no-preference){
  .vl-pin-ring{animation:vl-ping 2.8s ease-out infinite;}
  .vl-pin-ring:nth-child(2){animation-delay:1.4s;}
}
@keyframes vl-ping{from{transform:scale(.6);opacity:.7;}to{transform:scale(2.4);opacity:0;}}

/* footer */
.vl-footer{background:var(--ink-3);color:var(--on-ink);padding:56px 0 40px;}
.vl-footer-row{display:flex;flex-wrap:wrap;justify-content:space-between;gap:32px;padding-bottom:36px;border-bottom:1px solid var(--line-ink);}
.vl-footer p{font-size:14px;color:var(--on-ink-mute);max-width:38ch;margin-top:14px;}
.vl-footer nav{display:flex;flex-wrap:wrap;gap:12px 28px;align-items:flex-start;}
.vl-footer nav a{font-size:13px;letter-spacing:.08em;text-decoration:none;color:var(--on-ink-mute);}
.vl-footer nav a:hover{color:var(--on-ink);}
.vl-footer .vl-copy{padding-top:24px;margin-top:0;max-width:none;font-size:13px;color:var(--on-ink-mute);}

/* responsive */
@media (max-width:860px){
  .vl-section{padding:72px 0;}
  .vl-hero{padding:56px 0 72px;}
  .vl-hero-grid,.vl-office{grid-template-columns:1fr;gap:48px;}
  .vl-panel{padding:32px 24px 28px;}
}
@media (max-width:640px){
  .vl-fields{grid-template-columns:1fr;}
  .vl-tabs{gap:0 20px;}
}
@media (max-width:560px){
  .vl-wrap{padding:0 20px;}
  .vl-form-foot .vl-btn{width:100%;}
}
`;

/* ───────────────────────── HELPERS ───────────────────────── */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MOBILE_RE = /^\+?[\d\s-]{10,15}$/;

function validate(type, values) {
  const errors = {};
  type.fields.forEach((f) => {
    const raw = values[f.name];
    const v = typeof raw === "string" ? raw.trim() : raw;
    const empty = v === undefined || v === null || v === "";

    if (empty) {
      if (!f.optional) errors[f.name] = f.type === "file" ? "Please attach your CV." : `Please enter ${f.label.toLowerCase()}.`;
      return;
    }
    if (f.type === "email" && !EMAIL_RE.test(v)) errors[f.name] = "Enter a valid email address, like name@example.com.";
    if (f.type === "tel" && !MOBILE_RE.test(v)) errors[f.name] = "Enter a valid mobile number (10 to 15 digits).";
    if (f.type === "url") {
      try {
        const u = new URL(v);
        if (!/^https?:$/.test(u.protocol)) throw new Error("bad");
      } catch {
        errors[f.name] = "Enter a full link starting with https://";
      }
    }
    if (f.type === "number") {
      const n = Number(v);
      if (Number.isNaN(n) || (f.min !== undefined && n < f.min) || (f.max !== undefined && n > f.max)) {
        errors[f.name] = `Enter a number${f.min !== undefined ? ` from ${f.min}` : ""}${f.max !== undefined ? ` to ${f.max}` : ""}.`;
      }
    }
    if (f.type === "file") {
      const okType = /\.(pdf|docx?)$/i.test(v.name);
      if (!okType) errors[f.name] = "Attach a PDF or Word file.";
      else if (v.size > MAX_CV_MB * 1024 * 1024) errors[f.name] = `The file is larger than ${MAX_CV_MB} MB.`;
    }
  });
  return errors;
}

function addBusinessDays(date, days) {
  const d = new Date(date);
  let left = days;
  while (left > 0) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) left -= 1;
  }
  return d;
}
const toDateOnly = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Where did this lead come from? ?source= / utm_source, else the referring site, else the default.
function detectSource() {
  if (typeof window === "undefined") return DEFAULT_SOURCE;
  const p = new URLSearchParams(window.location.search);
  const tagged = p.get("source") || p.get("utm_source");
  if (tagged) return tagged;
  try {
    if (document.referrer) {
      const host = new URL(document.referrer).hostname;
      if (host && host !== window.location.hostname) return `Referral – ${host}`;
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_SOURCE;
}

// One record shape for every form, so the CRM / lead tracker gets consistent columns.
function buildLead(type, values) {
  const now = new Date();
  const fields = {};
  type.fields.forEach((f) => {
    if (f.type === "file") {
      const file = values[f.name];
      fields[f.name] = file ? file.name : "";
    } else {
      fields[f.name] = (values[f.name] || "").toString().trim();
    }
  });
  return {
    leadType: type.id, // student | college | corporate | faculty
    source: detectSource(),
    createdAt: now.toISOString(),
    owner: OWNER_BY_TYPE[type.id] || "Unassigned",
    status: "New",
    nextFollowUpDate: toDateOnly(addBusinessDays(now, FOLLOW_UP_BUSINESS_DAYS)),
    sourcePage: typeof window !== "undefined" ? window.location.pathname : "",
    fields,
  };
}

async function sendToCrm(lead, cvFile) {
  if (!CRM_ENDPOINT) {
    // Demo mode: no endpoint configured yet.
    // eslint-disable-next-line no-console
    console.info("[VALMIKI lead — demo mode, set CRM_ENDPOINT to send]", lead, cvFile || "");
    await new Promise((r) => setTimeout(r, 600));
    return;
  }
  const body = new FormData();
  body.append("lead", JSON.stringify(lead));
  if (cvFile) body.append("cv", cvFile);
  const res = await fetch(CRM_ENDPOINT, { method: "POST", body });
  if (!res.ok) throw new Error(`CRM responded ${res.status}`);
}

/* ───────────────────────── FORM PIECES ───────────────────────── */

function Field({ id, field, value, error, index, onChange }) {
  const errId = `${id}-err`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errId : null, field.hint ? hintId : null].filter(Boolean).join(" ") || undefined;
  const common = {
    id,
    name: field.name,
    className: "vl-input",
    "aria-invalid": error ? "true" : undefined,
    "aria-describedby": describedBy,
  };

  let control;
  if (field.type === "textarea") {
    control = <textarea {...common} value={value || ""} onChange={(e) => onChange(field.name, e.target.value)} />;
  } else if (field.type === "select") {
    control = (
      <select {...common} value={value || ""} onChange={(e) => onChange(field.name, e.target.value)}>
        <option value="" disabled>Select one</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else if (field.type === "file") {
    control = (
      <input
        {...common}
        type="file"
        accept={field.accept}
        onChange={(e) => onChange(field.name, e.target.files && e.target.files[0] ? e.target.files[0] : null)}
      />
    );
  } else {
    control = (
      <input
        {...common}
        type={field.type}
        inputMode={field.type === "tel" ? "tel" : field.type === "number" ? "numeric" : undefined}
        autoComplete={field.autoComplete}
        placeholder={field.placeholder}
        min={field.min}
        max={field.max}
        value={value || ""}
        onChange={(e) => onChange(field.name, e.target.value)}
      />
    );
  }

  return (
    <div className={`vl-field${field.span === "full" ? " full" : ""}`} style={{ "--i": index }}>
      <label className="vl-label" htmlFor={id}>
        <span>{field.label}</span>
        {field.optional && <span className="vl-opt">Optional</span>}
      </label>
      {control}
      {field.hint && <p className="vl-hint" id={hintId}>{field.hint}</p>}
      {error && <p className="vl-error" id={errId}>{error}</p>}
    </div>
  );
}

function LeadForm({ type, values, setValues, onReset, titleRef }) {
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const formRef = useRef(null);
  const hpRef = useRef(null);

  const onChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validate(type, values);
    setErrors(found);
    if (Object.keys(found).length) {
      requestAnimationFrame(() => {
        const first = formRef.current && formRef.current.querySelector('[aria-invalid="true"]');
        if (first) first.focus();
      });
      return;
    }
    // Spam trap: real people never see or fill this field.
    if (hpRef.current && hpRef.current.value) {
      setStatus("success");
      return;
    }
    setStatus("sending");
    try {
      const cvField = type.fields.find((f) => f.type === "file");
      await sendToCrm(buildLead(type, values), cvField ? values[cvField.name] : null);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    const who = (values.fullName || values.name || "").toString().trim().split(" ")[0];
    return (
      <div className="vl-panel" role="status">
        <div className="vl-done">
          <div className="vl-done-mark" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 12 12" fill="none">
              <path d="M2.5 6.4L5 8.9L9.6 3.6" stroke="#C8A24A" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h3 ref={titleRef} tabIndex={-1}>Thank you{who ? `, ${who}` : ""}.</h3>
          <p className="vl-lead">
            We have received your details for &ldquo;{type.cta}&rdquo;. The VALMIKI team will follow up with you.
          </p>
          <button type="button" className="vl-btn vl-btn-line-ink" onClick={onReset}>Send another enquiry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="vl-panel">
      <h3 className="vl-form-title" ref={titleRef} tabIndex={-1}>{type.cta}</h3>
      <p className="vl-form-note">All fields are required unless marked optional.</p>

      <form ref={formRef} onSubmit={submit} noValidate>
        <div className="vl-hp" aria-hidden="true">
          <label>Leave this field empty<input ref={hpRef} type="text" name="website" tabIndex={-1} autoComplete="off" /></label>
        </div>

        <div className="vl-fields">
          {type.fields.map((f, i) => (
            <Field
              key={f.name}
              id={`vl-${type.id}-${f.name}`}
              field={f}
              index={i}
              value={values[f.name]}
              error={errors[f.name]}
              onChange={onChange}
            />
          ))}
        </div>

        {status === "error" && (
          <p className="vl-alert" role="alert">
            We could not send your details. Please check your connection and try again.
          </p>
        )}

        <div className="vl-form-foot">
          <button type="submit" className="vl-btn vl-btn-gold" disabled={status === "sending"}>
            {status === "sending" ? "Sending…" : type.cta}
          </button>
          <p className="vl-privacy">We will use these details only to respond to your enquiry.</p>
        </div>
      </form>
    </div>
  );
}

/* ───────────────────── NAVBAR + FOOTER (inline) ───────────────────── */

const VALMIKI_ROUTES = {
  home: "/valmiki",
  about: "/valmiki/about",
  program: "/valmiki/program",
  lab: "/valmiki/lab",
  partner: "/valmiki/partner",
  contact: "/valmiki/contact",
};

// CONTACT is the active page here.
const VALMIKI_NAV_LINKS = [
  { label: "HOME", href: VALMIKI_ROUTES.home },
  { label: "ABOUT VALMIKI", href: VALMIKI_ROUTES.about },
  { label: "PROGRAM", href: VALMIKI_ROUTES.program },
  { label: "VALMIKI AI LAB", href: VALMIKI_ROUTES.lab },
  { label: "PARTNER WITH US", href: VALMIKI_ROUTES.partner },
  { label: "CONTACT", href: VALMIKI_ROUTES.contact, current: true },
];

const ValmikiBrandMark = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true">
    <circle cx="15" cy="15" r="14" stroke="#C8A24A" strokeWidth="1.2" />
    <path d="M9 19L15 8L21 19" stroke="#F3F5F8" strokeWidth="1.4" strokeLinejoin="round" />
    <circle cx="15" cy="15.5" r="1.6" fill="#C8A24A" />
  </svg>
);

/* Contact popup used by the gold "CONTACT WITH US" CTA */
function ContactPopup({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="vl-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vl-modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="vl-modal">
        <button type="button" className="vl-modal-close" aria-label="Close contact form" onClick={onClose}>
          ✕
        </button>
        <h2 id="vl-modal-title">Contact with us</h2>
        <p>Tell us a little about you and we&apos;ll get back within 1–2 working days.</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // TODO: wire to backend / email service
            onClose();
          }}
        >
          <div>
            <label htmlFor="vl-m-name">Full name</label>
            <input id="vl-m-name" name="name" type="text" required autoComplete="name" />
          </div>
          <div>
            <label htmlFor="vl-m-email">Email</label>
            <input id="vl-m-email" name="email" type="email" required autoComplete="email" />
          </div>
          <div>
            <label htmlFor="vl-m-msg">Message</label>
            <textarea id="vl-m-msg" name="message" required />
          </div>
          <button type="submit" className="vl-btn vl-btn-gold">Send message</button>
        </form>
      </div>
    </div>
  );
}



/* ───────────────────────── PAGE ───────────────────────── */

export default function ValmikiContact() {
  const [typeId, setTypeId] = useState("student");
  // Answers are kept per form, so switching tabs never loses what someone already typed.
  const [allValues, setAllValues] = useState({ student: {}, college: {}, corporate: {}, faculty: {} });
  const [resetKey, setResetKey] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const titleRef = useRef(null);
  const sectionRef = useRef(null);

  const type = useMemo(() => TYPES.find((t) => t.id === typeId), [typeId]);

  // Open the right form from ?type=… on arrival.
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("type");
    const id = wanted && TYPE_ALIASES[wanted.toLowerCase()];
    if (id) setTypeId(id);
  }, []);

  const setValuesFor = (id) => (updater) =>
    setAllValues((prev) => ({ ...prev, [id]: typeof updater === "function" ? updater(prev[id]) : updater }));

  const choose = (id, scroll) => {
    setTypeId(id);
    if (scroll && sectionRef.current) {
      const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      sectionRef.current.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
      window.setTimeout(() => titleRef.current && titleRef.current.focus({ preventScroll: true }), reduce ? 0 : 500);
    }
  };

  const onReset = () => {
    setAllValues((prev) => ({ ...prev, [typeId]: {} }));
    setResetKey((k) => k + 1);
  };

  const onTabKey = (e) => {
    const i = TYPES.findIndex((t) => t.id === typeId);
    let next = null;
    if (e.key === "ArrowRight") next = TYPES[(i + 1) % TYPES.length];
    if (e.key === "ArrowLeft") next = TYPES[(i - 1 + TYPES.length) % TYPES.length];
    if (next) {
      e.preventDefault();
      setTypeId(next.id);
      const btn = document.getElementById(`vl-tab-${next.id}`);
      if (btn) btn.focus();
    }
  };

  return (
    <div className="vl-root" id="top">
      <style>{css}</style>

      <main>
        {/* HERO */}
        <section className="vl-hero" aria-labelledby="vl-contact-title">
          <div className="vl-hero-fx" aria-hidden="true"><span className="vl-hero-glow" /></div>
          <div className="vl-wrap vl-hero-grid">
            <div>
              <h1 className="vl-h1" id="vl-contact-title">Let&rsquo;s Build the Future of AI Learning Together.</h1>
              <p className="vl-lead">Choose how you would like to connect with VALMIKI AI LAB.</p>
            </div>

            <div className="vl-choose" role="group" aria-label="Choose how you would like to connect">
              <div className="vl-choose-head">
                <span className="vl-choose-title">I am a&hellip;</span>
                <span className="vl-choose-tag">4 ways to connect</span>
              </div>
              {TYPES.map((t, i) => (
                <button
                  key={t.id}
                  type="button"
                  className="vl-choice"
                  style={{ "--i": i }}
                  aria-pressed={typeId === t.id}
                  onClick={() => choose(t.id, true)}
                >
                  <span className="vl-choice-name">{t.tab}</span>
                  <span className="vl-choice-cta">{t.cta}</span>
                  <span className="vl-choice-blurb">{t.blurb}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* FORMS */}
        <section className="vl-section" id="vl-form" ref={sectionRef} aria-label="Contact forms">
          <div className="vl-wrap">
            <div className="vl-form-wrap">
              <div className="vl-tabs" role="tablist" aria-label="Type of enquiry" onKeyDown={onTabKey}>
                {TYPES.map((t) => (
                  <button
                    key={t.id}
                    id={`vl-tab-${t.id}`}
                    type="button"
                    role="tab"
                    className="vl-tab"
                    aria-selected={typeId === t.id}
                    aria-controls="vl-form-panel"
                    tabIndex={typeId === t.id ? 0 : -1}
                    onClick={() => setTypeId(t.id)}
                  >
                    {t.tab}
                  </button>
                ))}
              </div>

              <div id="vl-form-panel" role="tabpanel" aria-labelledby={`vl-tab-${typeId}`}>
                <LeadForm
                  key={`${typeId}-${resetKey}`}
                  type={type}
                  values={allValues[typeId]}
                  setValues={setValuesFor(typeId)}
                  onReset={onReset}
                  titleRef={titleRef}
                />
              </div>
            </div>
          </div>
        </section>

        {/* OFFICE LOCATION */}
        <section className="vl-section vl-dark" id="vl-office" aria-labelledby="vl-office-title">
          <div className="vl-wrap vl-office">
            <div>
              <h2 className="vl-h2" id="vl-office-title">Visit our office</h2>
              <p className="vl-office-name">{OFFICE.name}</p>
              <address className="vl-office-addr">
                {OFFICE.lines.map((line) => (
                  <React.Fragment key={line}>{line}<br /></React.Fragment>
                ))}
                {OFFICE.cityLine}
              </address>

              {(OFFICE.phone || OFFICE.email || OFFICE.hours) && (
                <ul className="vl-office-meta">
                  {OFFICE.phone && (
                    <li><span>Phone</span><a href={`tel:${OFFICE.phone.replace(/[^\d+]/g, "")}`}>{OFFICE.phone}</a></li>
                  )}
                  {OFFICE.email && (
                    <li><span>Email</span><a href={`mailto:${OFFICE.email}`}>{OFFICE.email}</a></li>
                  )}
                  {OFFICE.hours && (
                    <li><span>Hours</span><span>{OFFICE.hours}</span></li>
                  )}
                </ul>
              )}
            </div>

            <div className="vl-pinbox">
              <div className="vl-pin" aria-hidden="true">
                <span className="vl-pin-ring" />
                <span className="vl-pin-ring" />
                <svg width="34" height="44" viewBox="0 0 34 44" fill="none">
                  <path d="M17 2C9 2 3 8.2 3 15.8 3 26 17 42 17 42s14-16 14-26.2C31 8.2 25 2 17 2z"
                        fill="#C8A24A" stroke="#E0BC62" strokeWidth="1.5" />
                  <circle cx="17" cy="16" r="5" fill="#0B1626" />
                </svg>
              </div>
              <a
                className="vl-btn vl-btn-gold"
                href={
                  OFFICE.mapUrl ||
                  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    [...OFFICE.lines, OFFICE.cityLine].join(", ")
                  )}`
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                Get directions
              </a>
            </div>
          </div>
        </section>
      </main>

      <ValmikiFooter />
      <ContactPopup open={contactOpen} onClose={() => setContactOpen(false)} />

      {/* Lead popup — ValmikiLeadForm opens itself after OPEN_AFTER_MS (see that file) */}
      <ValmikiLeadForm />
    </div>
  );
}